import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, fmtDateTime, timeAgo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { isPhoneDevice } from "@/lib/phone";
import { PHONE_MODE_KEY } from "@/components/PhoneBridge";
import { Smartphone, Download, KeyRound, Trash2, Search, PhoneIncoming, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3"><span className="w-8 h-8 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center"><Icon className="w-4 h-4" /></span><h2 className="font-heading font-bold text-slate-800">{title}</h2></div>
    {children}
  </section>
);

function PairCard({ onPaired }) {
  const [code, setCode] = useState(null);
  const gen = async () => {
    try {
      const { data } = await api.post("/mobile/pair-code"); setCode(data.code);
      let n = 0; const t = setInterval(() => { onPaired(); n += 1; if (n >= 120) clearInterval(t); }, 5000);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title="Pair the OHC CRM Android app" icon={KeyRound} testid="pair-card">
      <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
        <li>Install the app on your Android phone using the download below.</li>
        <li>Open <b>OHC CRM</b>, allow phone and call-log permissions, and tap <b>Pair with desktop</b>.</li>
        <li>Enter the 6-digit code shown here. It is valid for 10 minutes.</li>
      </ol>
      {window.OHCNative && <Button className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={() => window.OHCNative.openPairing()} data-testid="native-pair-btn"><Smartphone className="w-4 h-4 mr-2" />Open pairing on this phone</Button>}
      {code ? <div className="mt-4 text-4xl font-mono font-bold tracking-[0.4em] text-[#312E81]" data-testid="pair-code">{code}</div> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={gen} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="pair-code-btn"><KeyRound className="w-4 h-4 mr-2" />{code ? "New code" : "Show pairing code"}</Button>
        <a href="/downloads/ohc-crm.apk" download className="inline-flex items-center h-10 px-4 rounded-md border border-slate-200 text-sm font-medium hover:bg-slate-50" data-testid="apk-download"><Download className="w-4 h-4 mr-2" />Download Android app (APK)</a>
      </div>
    </Card>
  );
}

function Devices({ devices, reload }) {
  const unpair = async (d) => {
    if (!window.confirm(`Unpair ${d.device_name}?`)) return;
    try { await api.delete(`/mobile/devices/${d.id}`); toast.success("Phone unpaired"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title="Paired phones" icon={Smartphone} testid="devices-card">
      {!devices.length && <div className="text-sm text-slate-400" data-testid="no-devices">No phone paired yet. Desktop "Call" buttons need a paired phone.</div>}
      <div className="divide-y divide-slate-100">
        {devices.map((d) => (
          <div key={d.id} className="py-2.5 flex items-center gap-3" data-testid={`device-${d.id}`}>
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <div className="flex-1 min-w-0"><div className="text-sm font-semibold">{d.device_name}</div><div className="text-[11px] text-slate-400">Paired {fmtDateTime(d.paired_at)} · last active {timeAgo(d.last_seen)}</div></div>
            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => unpair(d)} data-testid={`unpair-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BrowserPhone() {
  const [on, setOn] = useState(localStorage.getItem(PHONE_MODE_KEY) === "1");
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  const toggle = (v) => { setOn(v); localStorage.setItem(PHONE_MODE_KEY, v ? "1" : "0"); toast.success(v ? "This phone will receive desktop calls while the CRM is open" : "Turned off"); };
  return (
    <Card title="Phone web app (Android & iPhone)" icon={Globe} testid="pwa-card">
      <p className="text-sm text-slate-600">Install the CRM on your home screen: Android Chrome menu → <b>Install app</b>; iPhone Safari Share → <b>Add to Home Screen</b>.</p>
      {prompt && <Button className="mt-3 bg-[#4F46E5] hover:bg-[#4338CA]" onClick={() => prompt.prompt()} data-testid="pwa-install-btn"><Download className="w-4 h-4 mr-2" />Install OHC CRM</Button>}
      {isPhoneDevice() ? (
        <label className="mt-4 flex items-center gap-3 text-sm" data-testid="phone-mode-row"><Switch checked={on} onCheckedChange={toggle} data-testid="phone-mode-switch" />Use this phone for desktop calls (one tap to dial)</label>
      ) : <p className="mt-3 text-xs text-slate-400">Open this page on your phone to use its browser as your calling phone.</p>}
    </Card>
  );
}

function Lookup() {
  const [phone, setPhone] = useState("");
  const [res, setRes] = useState(undefined);
  const go = async (e) => {
    e.preventDefault();
    try { const { data } = await api.get("/mobile/lookup", { params: { phone } }); setRes(data.contact); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Card title="Caller name check" icon={PhoneIncoming} testid="lookup-card">
      <p className="text-sm text-slate-600 mb-3">The Android app shows this name when a student, parent or prospect calls you.</p>
      <form onSubmit={go} className="flex gap-2"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter a mobile number" data-testid="lookup-input" /><Button type="submit" variant="outline" data-testid="lookup-btn"><Search className="w-4 h-4" /></Button></form>
      {res !== undefined && (res ? <div className="mt-3 p-3 rounded-xl bg-indigo-50" data-testid="lookup-result"><div className="font-bold text-[#312E81]">{res.name}</div><div className="text-xs text-slate-500">{[res.type, res.stage, res.course, res.city, res.assigned_name && `Owner: ${res.assigned_name}`].filter(Boolean).join(" · ")}</div></div>
        : <div className="mt-3 text-sm text-slate-400" data-testid="lookup-none">Not in CRM</div>)}
    </Card>
  );
}

export default function MyPhone() {
  const [devices, setDevices] = useState([]);
  const load = () => api.get("/mobile/devices").then(({ data }) => setDevices(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-5">
      <PageHeader title="My Phone" subtitle="Call students from the desktop CRM through your own phone, and see who is calling" testid="my-phone-header" />
      <div className="grid lg:grid-cols-2 gap-5"><PairCard onPaired={load} /><Devices devices={devices} reload={load} /><BrowserPhone /><Lookup /></div>
    </div>
  );
}
